import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, ImagePlus, Star, Store, Trash2, X } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/**
 * Katalog guest aplikacije (faza 4, `GUEST_HIBRID_API.md` § 8).
 *
 * - Objekti: uključivanje u katalog (10), „Istaknuto do" - sponzorisano mesto
 *   koje gost vidi označeno kao sponzorisano (16, 156), i fotografije koje
 *   postavlja podrška (24), sa brisanjem. Prva fotografija postaje naslovna ako je objekat nema.
 * - Recenzije: gost piše tek posle stvarne posete; objavljuje se tek posle
 *   odobrenja ovde (18). Napomena uz odluku ostaje u bazi, gost je ne vidi.
 *
 * Sve radnje idu u audit log na serveru. Prodavac (`reseller`) ovu stranu ne vidi.
 */

type CatalogLocation = {
  id: string;
  name: string;
  city: string | null;
  venueType: string | null;
  isListed: boolean;
  listedBy: string | null;
  featuredUntil: string | null;
  tenantId: string;
  tenantName: string;
};

type Review = {
  id: string;
  rating: number;
  text: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  locationId: string;
  locationName: string;
  guestEmail: string;
  guestName: string | null;
  visitKind: string | null;
};

const VENUE_TYPE: Record<string, string> = {
  restaurant: 'Restoran', cafe: 'Kafić', bar: 'Bar', club: 'Klub', pool: 'Bazen', beach: 'Plaža', hotel: 'Hotel', other: 'Ostalo',
};

const REVIEW_STATUS: Record<Review['status'], string> = { pending: 'Na čekanju', approved: 'Odobrene', rejected: 'Odbijene' };

const MAX_PHOTO_BYTES = 6 * 1024 * 1024;

const fmt = (iso: string) => new Date(iso).toLocaleString('sr-RS', { dateStyle: 'medium', timeStyle: 'short' });

/** `YYYY-MM-DD` za `<input type="date">` iz ISO vremena. */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function errMsg(e: any, fallback: string): string {
  return e?.data?.message || e?.message || fallback;
}

export function GuestCatalogPage() {
  const [tab, setTab] = useState<'locations' | 'reviews'>('locations');
  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Store size={22} /> Katalog guest aplikacije
        </h1>
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
          Koji objekti se vide u aplikaciji za goste, koji su istaknuti, njihove fotografije i recenzije gostiju.
        </div>
      </header>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={tab === 'locations' ? tabActive : tabBtn} onClick={() => setTab('locations')}>Objekti</button>
        <button style={tab === 'reviews' ? tabActive : tabBtn} onClick={() => setTab('reviews')}>Recenzije</button>
      </div>

      {tab === 'locations' ? <LocationsTab /> : <ReviewsTab />}
    </div>
  );
}

function LocationsTab() {
  const [rows, setRows] = useState<CatalogLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState('');
  /** Brojač po objektu: posle dodavanja fotografije spisak se ponovo učitava. */
  const [photoVersion, setPhotoVersion] = useState<Record<string, number>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadFor = useRef<CatalogLocation | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.get<CatalogLocation[]>('/admin/guest-catalog/locations'));
    } catch (e: any) {
      setError(errMsg(e, 'Objekti nisu učitani.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const patch = async (loc: CatalogLocation, body: { isListed?: boolean; featuredUntil?: string | null }, okText: string) => {
    setBusy(loc.id);
    setError(null);
    setInfo(null);
    try {
      await api.patch(`/admin/guest-catalog/locations/${loc.id}`, body);
      setInfo(okText);
      await refresh();
    } catch (e: any) {
      setError(errMsg(e, 'Izmena nije sačuvana.'));
    } finally {
      setBusy(null);
    }
  };

  const setFeatured = (loc: CatalogLocation, value: string) => {
    // Istaknuto važi do kraja izabranog dana.
    const featuredUntil = value ? new Date(`${value}T23:59:59`).toISOString() : null;
    void patch(loc, { featuredUntil }, value ? `${loc.name}: istaknuto do ${value.split('-').reverse().join('.')}.` : `${loc.name}: više nije istaknuto.`);
  };

  const pickPhoto = (loc: CatalogLocation) => {
    uploadFor.current = loc;
    fileRef.current?.click();
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const loc = uploadFor.current;
    e.target.value = '';
    if (!file || !loc) return;
    if (!file.type.startsWith('image/')) { setError('Izaberite sliku (JPG, PNG ili WebP).'); return; }
    if (file.size > MAX_PHOTO_BYTES) { setError('Slika je veća od 6 MB.'); return; }
    const caption = window.prompt('Opis fotografije (nije obavezno)', '') ?? '';
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(loc.id);
      setError(null);
      setInfo(null);
      try {
        await api.post(`/admin/guest-catalog/locations/${loc.id}/photos`, { dataUrl: String(reader.result), caption: caption.trim() });
        setPhotoVersion((v) => ({ ...v, [loc.id]: (v[loc.id] ?? 0) + 1 }));
        setInfo(`${loc.name}: fotografija je dodata.`);
      } catch (err: any) {
        setError(errMsg(err, 'Fotografija nije dodata.'));
      } finally {
        setBusy(null);
      }
    };
    reader.onerror = () => setError('Slika nije učitana.');
    reader.readAsDataURL(file);
  };

  const now = Date.now();
  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    return !s || [r.name, r.tenantName, r.city ?? ''].some((x) => x.toLowerCase().includes(s));
  });

  return (
    <>
      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {info && <div style={infoBox}>{info}</div>}

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Pretraga po objektu, klijentu ili gradu"
        style={{ ...inputStyle, maxWidth: 360, marginBottom: 12 }}
      />
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />

      <div style={tableWrap}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Objekat</th>
                <th style={th}>Klijent</th>
                <th style={th}>Vrsta</th>
                <th style={th}>U katalogu</th>
                <th style={th}>Istaknuto do</th>
                <th style={th}>Fotografije</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const featured = !!r.featuredUntil && new Date(r.featuredUntil).getTime() > now;
                return (
                  <tr key={r.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>{r.name}</div>
                      {r.city && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{r.city}</div>}
                    </td>
                    <td style={td}>{r.tenantName}</td>
                    <td style={td}>{r.venueType ? VENUE_TYPE[r.venueType] ?? r.venueType : '-'}</td>
                    <td style={td}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: busy ? 'default' : 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={r.isListed}
                          disabled={busy === r.id}
                          onChange={(e) => patch(r, { isListed: e.target.checked }, e.target.checked ? `${r.name} je u katalogu.` : `${r.name} je sklonjen iz kataloga.`)}
                        />
                        <span>{r.isListed ? 'Da' : 'Ne'}</span>
                      </label>
                      {r.isListed && r.listedBy && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                          {r.listedBy === 'admin' ? 'uključila podrška' : 'uključio vlasnik'}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <FeaturedInput
                        value={toDateInput(r.featuredUntil)}
                        disabled={busy === r.id}
                        label={`Istaknuto do, ${r.name}`}
                        onCommit={(v) => setFeatured(r, v)}
                      />
                      {featured && <span style={{ ...pill, marginTop: 4, background: 'rgba(234,179,8,0.15)', color: '#eab308' }}><Star size={10} /> Sponzorisano</span>}
                    </td>
                    <td style={td}>
                      <PhotosCell
                        loc={r}
                        version={photoVersion[r.id] ?? 0}
                        disabled={busy === r.id}
                        onAdd={() => pickPhoto(r)}
                        onError={setError}
                      />
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td style={{ ...td, textAlign: 'center', color: 'var(--color-text-muted)' }} colSpan={6}>Nema restoranskih objekata.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

type LocationPhoto = { id: string; url: string; caption: string; uploadedBy: string; sortOrder: number };

/**
 * Fotografije objekta: spisak se učitava tek na „Prikaži" (objekata može biti
 * stotine), a posle dodavanja se otvara sam.
 */
function PhotosCell({ loc, version, disabled, onAdd, onError }: {
  loc: CatalogLocation; version: number; disabled: boolean; onAdd: () => void; onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<LocationPhoto[] | null>(null);
  const [deleting, setDeleting] = useState<LocationPhoto | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await api.get<LocationPhoto[]>(`/admin/guest-catalog/locations/${loc.id}/photos`);
      setList([...rows].sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (e: any) {
      onError(errMsg(e, 'Fotografije nisu učitane.'));
    }
  }, [loc.id, onError]);

  useEffect(() => { if (version > 0) setOpen(true); }, [version]);
  useEffect(() => { if (open) void load(); }, [open, version, load]);

  const remove = async () => {
    if (!deleting) return;
    try {
      await api.del(`/admin/guest-catalog/locations/${loc.id}/photos/${deleting.id}`);
      await load();
    } catch (e: any) {
      onError(errMsg(e, 'Fotografija nije obrisana.'));
    }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={btnSecondary} disabled={disabled} onClick={onAdd}><ImagePlus size={14} /> Dodaj</button>
        <button style={btnSecondary} onClick={() => setOpen((v) => !v)}>
          {open ? 'Sakrij' : 'Prikaži'}{list ? ` (${list.length})` : ''}
        </button>
      </div>
      {open && list && (
        list.length === 0
          ? <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6 }}>Nema fotografija.</div>
          : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {list.map((ph) => (
                <div key={ph.id} style={{ position: 'relative' }} title={[ph.caption, ph.uploadedBy === 'admin' ? 'postavila podrška' : 'postavio vlasnik'].filter(Boolean).join(' · ')}>
                  <img src={ph.url} alt={ph.caption} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                  <button
                    onClick={() => setDeleting(ph)}
                    title="Obriši fotografiju"
                    aria-label="Obriši fotografiju"
                    style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.65)', border: 'none', color: '#fff', borderRadius: 4, padding: 2, cursor: 'pointer', lineHeight: 0 }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )
      )}
      {deleting && (
        <ConfirmDialog
          title="Obrisati fotografiju?"
          message={`Fotografija se uklanja iz profila objekta ${loc.name} u aplikaciji za goste.`}
          confirmLabel="Obriši"
          onConfirm={remove}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

/**
 * Datum se čuva tek kad se polje napusti (ili Enter): dok se kuca, polje je
 * privremeno prazno, a prazno znači „ukloni isticanje".
 */
function FeaturedInput({ value, disabled, label, onCommit }: {
  value: string; disabled: boolean; label: string; onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = () => { if (draft !== value) onCommit(draft); };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="date"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
        style={{ ...inputStyle, width: 150 }}
        aria-label={label}
      />
      {value && (
        <button style={iconBtn} title="Ukloni isticanje" disabled={disabled} onClick={() => onCommit('')}><X size={15} /></button>
      )}
    </div>
  );
}

function ReviewsTab() {
  const [status, setStatus] = useState<Review['status']>('pending');
  const [rows, setRows] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<{ review: Review; decision: 'approved' | 'rejected' } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.get<Review[]>(`/admin/guest-catalog/reviews?status=${status}`));
    } catch (e: any) {
      setError(errMsg(e, 'Recenzije nisu učitane.'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { refresh(); }, [refresh]);

  const moderate = async (note?: string) => {
    if (!target) return;
    try {
      await api.post(`/admin/guest-catalog/reviews/${target.review.id}/moderate`, { status: target.decision, note: note?.trim() || undefined });
      await refresh();
    } catch (e: any) {
      setError(errMsg(e, 'Odluka nije sačuvana.'));
    }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(Object.keys(REVIEW_STATUS) as Review['status'][]).map((s) => (
          <button key={s} style={status === s ? tabActive : tabBtn} onClick={() => setStatus(s)}>{REVIEW_STATUS[s]}</button>
        ))}
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((r) => (
          <div key={r.id} style={card}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>{r.locationName}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {r.guestName || 'Gost'} · {r.guestEmail} · {fmt(r.createdAt)}
                  {r.visitKind ? ` · poseta: ${r.visitKind === 'reservation' ? 'rezervacija' : 'porudžbina'}` : ''}
                </div>
              </div>
              <div style={{ color: '#eab308', whiteSpace: 'nowrap' }} aria-label={`Ocena ${r.rating} od 5`}>
                {'★'.repeat(r.rating)}<span style={{ color: 'var(--color-border)' }}>{'★'.repeat(Math.max(0, 5 - r.rating))}</span>
              </div>
            </div>
            {r.text && <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--color-text)', whiteSpace: 'pre-wrap' }}>{r.text}</p>}
            {r.status === 'pending' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button style={btnPrimary} onClick={() => setTarget({ review: r, decision: 'approved' })}><Check size={14} /> Odobri</button>
                <button style={btnDanger} onClick={() => setTarget({ review: r, decision: 'rejected' })}><X size={14} /> Odbij</button>
              </div>
            )}
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
            {status === 'pending' ? 'Nema recenzija koje čekaju odluku.' : 'Nema recenzija.'}
          </div>
        )}
      </div>

      {target && (
        <ConfirmDialog
          title={target.decision === 'approved' ? 'Odobriti recenziju?' : 'Odbiti recenziju?'}
          message={target.decision === 'approved'
            ? `Recenzija za ${target.review.locationName} postaje vidljiva svim gostima.`
            : `Recenzija za ${target.review.locationName} se ne objavljuje.`}
          confirmLabel={target.decision === 'approved' ? 'Odobri' : 'Odbij'}
          variant={target.decision === 'approved' ? 'default' : 'danger'}
          input={{ label: 'Napomena (nije obavezno, gost je ne vidi)', placeholder: 'npr. uvredljiv sadržaj', multiline: true }}
          onConfirm={moderate}
          onClose={() => setTarget(null)}
        />
      )}
    </>
  );
}

const tableWrap: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 14 };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, borderRadius: 4, cursor: 'pointer' };
const pill: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 };
const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnDanger: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const tabBtn: React.CSSProperties = { padding: '8px 14px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const tabActive: React.CSSProperties = { ...tabBtn, background: 'var(--color-primary)', borderColor: 'var(--color-primary)', color: '#fff', fontWeight: 600 };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const infoBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.12)', color: '#86efac', fontSize: 13, marginBottom: 16 };
